import moment from 'moment';
import { LOCALE_EN_CA } from 'src/constants';

export const formatTimestamps = (inputs: number[]): string[] => {
    return inputs
        .map((timestamp) => {
            // Timestamps should return in format 'YYYY-MM-DD HH:MM:SS' (e.g. '2021-09-18 05:36:07')
            return new Date(timestamp).toISOString().substring(0, 19).replace(/T/g, ' ');
        })
        .reverse();
};

export const formatFullDateToCodeReadable = (date: string): string => {
    // Input date in format YYYY-MM-DD and we return yyyyMM
    const [year, month, day] = date.split('-');
    return `${year}${month}`;
};

export const formatDateToCodeReadable = (date: string): string => {
    // Input date in format of MM/yyyy and we return yyyyMM
    const [month, year] = date.split('/');
    return `${year}${month}`;
};

export const formatDateToHumanReadable = (date: string): string => {
    // Input date in format of yyyyMM and we return MM/yyyy
    return `${date.slice(4)}/${date.slice(0, 4)}`;
};

export const humanReadableDateIsCorrectlyFormatted = (date: string): boolean => {
    // Checks that input date is in format of MM/yyyy
    return /^(0[1-9]|1[012])[/](19|20|21)\d\d$/.test(date);
};

export const periodFormatter = (date: string): string => {
    // date should come in the format 'YYYY-MM-DD'
    const dateElements: string[] = date.split('-');
    const period: string = `${dateElements[1]}${dateElements[0]}`;

    return period;
};

export const getCurrentTimestamp = (): string => {
    let date_obj = moment().utc();

    // Should be in format 'YYYY-MM-DD HH:MM:SS' (e.g. '2021-09-18 05:36:07').
    // The capital HH signifies a 24-hour format, which matches the database timestamp formatting.
    return date_obj.format('YYYY-MM-DD HH:mm:ss');
};

export const getCurrentDate = (): string => {
    const now = new Date();

    // outputs a date in the format 'YYYY-MM-DD' (e.g. '2021-09-18')
    return now.toISOString().split('T')[0];
};

export const timestampFormatter = (input: number): string => {
    // Inputs should be in form number of milliseconds (e.g. 1632933684000)
    // Timestamps should return in format 'YYYY-MM-DD HH:MM:SS' (e.g. '2021-09-18 05:36:07')
    return new Date(input).toISOString().substring(0, 19).replace(/T/g, ' ');
};

export const swapPeriodYearAndMonth = (period: string): string => {
    // period will be in the format 'MMYYYY';
    const currMonth = period.substr(0, 2);
    const currYear = period.substr(2);

    // Output should be in the format 'YYYYMM';
    return `${currYear}${currMonth}`;
};

// Generates an array of years consisting of all years between the specified years
export const generateArrayOfYears = (earliestYear: number, latestYear: number) => {
    let arrayOfYears = [];

    for (let i = earliestYear; i <= latestYear; i += 1) {
        arrayOfYears.push(i);
    }

    return arrayOfYears;
};

export const dateYYYYMMReg = /([12]\d{3}-(0[1-9]|1[0-2]))/;

export const getFirstDayOfMonth = (dateStr: string) => {
    return `${dateStr}-01`;
};

export const getLastDayOfMonth = (dateStr: string) => {
    if (!dateYYYYMMReg.test(dateStr)) return dateStr;
    const [yearStr, monthStr] = dateStr.split('-');
    const lastDayOfMonthDate = new Date(Number(yearStr), Number(monthStr), 0);
    const [month, day, year] = lastDayOfMonthDate.toLocaleDateString().split('/');
    return `${year}-${Number(month) < 10 ? '0' + month : month}-${day}`;
};

export const getDateAndTimestamp = (input: string | Date | undefined): [Date | null, number] => {
    let date: Date | null = null;
    let timestamp: number = Infinity;

    if (typeof input === 'string') {
        if (!input) return [null, Infinity];
        const [yearStr, monthStr, dayStr] = input.split('-');
        date = new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr));
        timestamp = date.getTime();
    } else if (input instanceof Date) {
        date = input;
        timestamp = date.getTime();
    }

    return [date, timestamp];
};

export const addDays = (date: Date | null, days: number): Date => {
    const result = date ? new Date(date) : new Date();
    result.setDate(result.getDate() + days);
    return result;
};

export const deductDays = (date: Date | null, days: number): Date => {
    const result = date ? new Date(date) : new Date();
    result.setDate(result.getDate() - days);
    return result;
};

export const getYearMonthDayFormatFromISOString = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

export const getCurrentYearMonth = (separator: string = '') => {
    const d = new Date();
    const monthNum = d.getMonth() + 1;
    return `${d.getFullYear()}${separator}${monthNum < 10 ? '0' + monthNum : monthNum}`;
};

export const convertDateToYyyyMmDdFormat = (date: Date | undefined, delimiter = false) => {
    if (!date) return '';
    let dateString = date.toISOString().slice(0, 10);
    if (!delimiter) {
        dateString = dateString.replaceAll('-', '');
    }
    return dateString;
};

export const convertTimestampToYYYYMMDDInLocalTimezone = (timestamp: Date) => {
    const dateObj = new Date(timestamp);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // 'en-CA' indicates YYYY-MM-DD format
    return dateObj.toLocaleString(LOCALE_EN_CA, {
        timeZone: timeZone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
    });
};

export const convertTimestampToYYYYMMDDInUTCTimezone = (timestamp: Date) => {
    const dateObj = new Date(timestamp);

    // 'en-CA' indicates YYYY-MM-DD format
    return dateObj.toLocaleString(LOCALE_EN_CA, {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
    });
};

export const convertTimestampToYYYYMMDDUTCTimeZone = (inputDate: Date) => {
    const year = inputDate?.getFullYear();
    const month = inputDate ? inputDate.getMonth() + 1 : 0;
    const day = inputDate?.getDate();
    const hours = inputDate?.getHours();
    const minutes = inputDate?.getMinutes();

    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hours).padStart(
        2,
        '0'
    )}:${String(minutes).padStart(2, '0')} UTC`;
};

export const convertDateStringToDateObj = (dateStr: string | undefined) => {
    return dateStr ? new Date(Date.parse(dateStr)) : new Date();
};

/**
 * Formats a UTC date object into a human-readable format: "Jun 24, 2025 at 2:45 PM"
 *
 * @param {Date|string|number} inputDate - The date to format (Date object, ISO string, or timestamp)
 * @param {boolean} includeYear - Whether to include the year in the output (default: true)
 * @param {boolean} includeTime - Whether to include the time in the output (default: true)
 * @return {string} Formatted date string
 */
export const formatDateFriendly = (inputDate: Date, includeYear = true, includeTime = true) => {
    if (!inputDate) {
        return '';
    }

    // Convert input to moment object and ensure it's in local timezone
    const momentDate = moment.utc(inputDate).local();

    // Check if the date is valid
    if (!momentDate.isValid()) {
        console.error('Invalid date provided:', inputDate);
        return '';
    }

    // Format the date part
    let formattedDate = momentDate.format('MMM D');

    // Add year if requested
    if (includeYear) {
        formattedDate += momentDate.format(', YYYY');
    }

    // Add time if requested
    if (includeTime) {
        formattedDate += momentDate.format(' [at] h:mm A');
    }

    return formattedDate;
};
